import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'

export type VulnerabilitySeverityCounts = {
  critical: number
  high: number
  medium: number
  low: number
  info: number
  total: number
}

export type CurrentVulnerabilityPosture = VulnerabilitySeverityCounts & {
  assetsTotal: number
  assetsWithVulnerabilities: number
  executiveOpenFindings: number
  openFindings: number
  currentUniqueFindings?: number
  currentAffectedInstances?: number
  informationSystemsTotal?: number
  sources: string[]
  scannerSources?: string[]
  contributingSources?: string[]
  severityDistribution?: {
    critical: number
    high: number
    medium: number
    low: number
    info: number
  }
  sinceLastScan?: {
    deltaUniqueFindings: number
    newestAssessmentAt?: string | null
    previousAssessmentAt?: string | null
  }
  lastAuthoritativeAssessment?: { id?: string; assessmentAt?: string } | null
  scopeMetadata?: Record<string, unknown>
  mostVulnerableAssets?: Array<{
    id: number
    name: string
    type: string
    uniqueFindings?: number
    affectedInstances?: number
    vulnerabilityCount?: number
    severity: VulnerabilitySeverityCounts
  }>
  topAssets: Array<{
    id: number
    name: string
    type: string
    vulnerabilityCount: number
    severity: VulnerabilitySeverityCounts
  }>
  keyVulnerabilities: Array<{
    id: number
    name: string
    title: string
    severity: string
    cvss?: number | null
    cve?: string | null
    asset: string
    assetName?: string | null
    sourceProvider?: string
    detectedBy?: string[]
    affectedInstances?: number
    lastDetectedAt?: string | null
    lastConfirmedAt?: string | null
    status?: string
  }>
  byAssetName?: Record<
    string,
    { name: string; critical: number; high: number; medium: number; low: number; info: number; total: number }
  >
}

export type VulnerabilityTrendPoint = {
  date: string
  detected: number
  assessments?: number
  fixed?: number
}

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('authToken') ||
        localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export async function fetchCurrentVulnerabilityPosture(): Promise<CurrentVulnerabilityPosture> {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.VULNERABILITY_POSTURE_CURRENT), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchVulnerabilityTrend(days = 90): Promise<VulnerabilityTrendPoint[]> {
  const url = withOrganizationScope(`${API_ENDPOINTS.VULNERABILITY_POSTURE_TREND}?days=${days}`)
  const res = await fetch(url, { headers: getAuthHeaders() })
  const body = await parseJson(res)
  return Array.isArray(body?.trend) ? body.trend : []
}

export function countAssetsWithSeverity(
  byAssetName: Record<string, { critical?: number; high?: number }> | undefined,
  severity: 'critical' | 'high',
): number {
  if (!byAssetName) return 0
  return Object.values(byAssetName).filter((a) => (a[severity] ?? 0) > 0).length
}

export function formatScannerSourceLabel(source: string): string {
  const key = String(source || '').toUpperCase()
  const map: Record<string, string> = {
    GREENBONE: 'Greenbone',
    OPENVAS: 'OpenVAS',
    OWASP_ZAP: 'OWASP ZAP',
    ZAP: 'ZAP',
    NUCLEI: 'Nuclei',
    UPLOADED_REPORT: 'Uploaded reports',
    UNKNOWN: 'Unknown',
  }
  return map[key] || source
}

export type AssessmentHistoryEntry = {
  assessmentId: string
  assessmentAt: string
  providerType: string
  sourceType: string
  sourceRefId: string
  severity: string
  title: string
  instanceCount?: number
}

export async function fetchAssessmentHistory(params: {
  assetKey?: string | null
  fingerprint?: string | null
  title?: string | null
}): Promise<AssessmentHistoryEntry[]> {
  const qs = new URLSearchParams()
  if (params.assetKey) qs.set('assetKey', params.assetKey)
  if (params.fingerprint) qs.set('fingerprint', params.fingerprint)
  if (params.title) qs.set('title', params.title)
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.VULNERABILITY_POSTURE_ASSESSMENT_HISTORY}?${qs}`), {
    headers: getAuthHeaders(),
  })
  const body = await parseJson(res)
  return Array.isArray(body?.history) ? body.history : []
}

export function formatScannerSources(sources: string[] | undefined | null): string {
  if (!sources?.length) return 'Unified'
  const unique = [...new Set(sources.map((s) => formatScannerSourceLabel(s)))]
  return unique.join(' · ')
}
